import React, { useState, useEffect, useRef } from 'react';
import { RouteComponentProps } from '@reach/router';
import clsx from 'clsx';
import { useAuthUser, useRequestEmailCode, useVerifyEmailCode } from '../queries/Accounts';
import { useLogout } from '../d-man';
import { useTheme } from '../contexts/ThemeContext';
import ThemeToggle from '../components/ThemeToggle';

declare global {
    interface Window {
        turnstile?: {
            render: (
                container: HTMLElement,
                options: {
                    sitekey: string;
                    theme?: 'light' | 'dark';
                    callback?: (token: string) => void;
                    'error-callback'?: () => void;
                    'expired-callback'?: () => void;
                }
            ) => string;
            reset: (widgetId?: string) => void;
            remove: (widgetId?: string) => void;
        };
    }
}

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';
const TURNSTILE_SCRIPT_ID = 'cf-turnstile-script';
const TURNSTILE_SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js';

function MailIcon() {
    return (
        <svg
            className="w-5 h-5"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
        >
            <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.75"
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
        </svg>
    );
}

function UserIcon() {
    return (
        <svg
            className="w-5 h-5"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
        >
            <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.75"
                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
            />
        </svg>
    );
}

function LockIcon() {
    return (
        <svg
            className="w-5 h-5"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
        >
            <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.75"
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zM8 11V7a4 4 0 118 0v4"
            />
        </svg>
    );
}

function ShieldIcon() {
    return (
        <svg
            className="w-5 h-5 text-orange-500"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
        >
            <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.75"
                d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
            />
        </svg>
    );
}

function SpinnerIcon() {
    return (
        <svg
            className="w-4 h-4 animate-spin"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
        >
            <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
            />
            <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
        </svg>
    );
}

type Step = 'email' | 'code' | 'password';

export default function Login(props: RouteComponentProps) {
    const [step, setStep] = useState<Step>('email');
    const [email, setEmail] = useState('');
    const [code, setCode] = useState('');
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [leaving, setLeaving] = useState(false);
    const [turnstileToken, setTurnstileToken] = useState('');
    const turnstileContainerRef = useRef<HTMLDivElement>(null);
    const turnstileWidgetId = useRef<string | undefined>(undefined);
    const logout = useLogout();
    const { theme } = useTheme();

    const authUser = useAuthUser();
    const requestCode = useRequestEmailCode();
    const verifyCode = useVerifyEmailCode();

    useEffect(() => {
        if (step !== 'email' || !TURNSTILE_SITE_KEY) return undefined;

        const renderWidget = () => {
            if (!turnstileContainerRef.current || !window.turnstile) return;
            turnstileWidgetId.current = window.turnstile.render(turnstileContainerRef.current, {
                sitekey: TURNSTILE_SITE_KEY,
                theme: theme === 'dark' ? 'dark' : 'light',
                callback: (token) => setTurnstileToken(token),
                'expired-callback': () => setTurnstileToken(''),
                'error-callback': () => setTurnstileToken(''),
            });
        };

        if (window.turnstile) {
            renderWidget();
        } else if (!document.getElementById(TURNSTILE_SCRIPT_ID)) {
            const script = document.createElement('script');
            script.id = TURNSTILE_SCRIPT_ID;
            script.src = TURNSTILE_SCRIPT_SRC;
            script.async = true;
            script.defer = true;
            script.onload = renderWidget;
            document.body.appendChild(script);
        } else {
            document.getElementById(TURNSTILE_SCRIPT_ID)?.addEventListener('load', renderWidget);
        }

        return () => {
            if (turnstileWidgetId.current && window.turnstile) {
                window.turnstile.remove(turnstileWidgetId.current);
                turnstileWidgetId.current = undefined;
            }
        };
    }, [step, theme]);

    useEffect(() => {
        if (requestCode.error) {
            setErrorMessage('Verification failed. Please try again.');
            setTurnstileToken('');
            if (turnstileWidgetId.current && window.turnstile) {
                window.turnstile.reset(turnstileWidgetId.current);
            }
        }
    }, [requestCode.error]);

    useEffect(() => {
        if (authUser.error) {
            setErrorMessage('Incorrect username or password');
        }
    }, [authUser.error]);

    useEffect(() => {
        if (verifyCode.error) {
            setErrorMessage('Invalid or expired code. Please try again.');
        }
    }, [verifyCode.error]);

    useEffect(() => {
        if (requestCode.success) {
            setErrorMessage('');
            setStep('code');
        }
    }, [requestCode.success]);

    useEffect(() => {
        const expired = (() => {
            if (!authUser.data) return false;
            const exp = authUser?.data?.user?.exp;
            if (!exp) return false;
            return new Date().getTime() / 1000 >= exp;
        })();

        if (authUser.success && props.navigate && !expired) {
            setLeaving(true);
            const { navigate } = props;
            const timer = setTimeout(() => navigate('/'), 300);
            return () => clearTimeout(timer);
        }
        return undefined;
    }, [authUser.data, authUser.success, props]);

    useEffect(() => {
        const expired = (() => {
            if (!verifyCode.data) return false;
            const exp = verifyCode?.data?.user?.exp;
            if (!exp) return false;
            return new Date().getTime() / 1000 >= exp;
        })();

        if (verifyCode.success && props.navigate && !expired) {
            setLeaving(true);
            const { navigate } = props;
            const timer = setTimeout(() => navigate('/'), 300);
            return () => clearTimeout(timer);
        }
        return undefined;
    }, [verifyCode.data, verifyCode.success, props]);

    const submitEmail = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setErrorMessage('');
        await requestCode.execute({ email, turnstileToken });
    };

    const submitCode = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setErrorMessage('');
        await verifyCode.execute({ email, code });
    };

    const submitPassword = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        logout();
        setErrorMessage('');
        await authUser.execute({ username, password });
    };

    const goToStep = (next: Step) => {
        setErrorMessage('');
        setStep(next);
    };

    const pageBg = theme === 'dark' ? '#2d3b46' : '#f1f2f4';
    const cardShadow =
        theme === 'dark'
            ? '0 1px 2px rgba(0,0,0,0.4), 0 20px 40px -12px rgba(0,0,0,0.5)'
            : '0 1px 2px rgba(15,15,15,0.04), 0 2px 8px rgba(15,15,15,0.04), 0 24px 48px -16px rgba(15,15,15,0.14)';

    // Buttons stay active regardless of field state - required attributes below
    // trigger the browser's native "please fill this in" validation on submit.
    /**
     * Turnstile fails closed on the server: an empty token is rejected before
     * anything else happens, so submitting without one returns an immediate
     * 400 that reads as "Verification failed". With no site key configured the
     * widget never renders either, so there was no captcha on screen to
     * explain the failure or retry. Block the submit and say why instead.
     */
    const turnstileMisconfigured = !TURNSTILE_SITE_KEY;
    const emailDisabled = requestCode.loading || turnstileMisconfigured || !turnstileToken;
    const codeDisabled = verifyCode.loading;
    const passwordDisabled = authUser.loading;

    const logoSrc = theme === 'dark' ? '/logo-dark.png' : '/logo-light.png';

    const errorBanner = errorMessage && (
        <div className="mt-4 flex items-start gap-2 rounded-lg bg-red-50 border border-red-100 px-3.5 py-2.5 text-sm text-red-600">
            <svg
                className="w-4 h-4 mt-0.5 flex-shrink-0"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
            >
                <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
            </svg>
            <span>{errorMessage}</span>
        </div>
    );

    return (
        <div
            className="min-h-screen w-full flex flex-col items-center justify-center px-4"
            style={{ backgroundColor: pageBg, transition: 'background-color 0.2s ease' }}
        >
            <div className="fixed top-4 right-4 sm:top-6 sm:right-6">
                <ThemeToggle />
            </div>

            <div className="flex flex-col items-center mb-8">
                <div className="flex items-center gap-2">
                    <img src={logoSrc} alt="Raubex BIC" className="h-8 w-auto" />
                    <div className="text-2xl font-bold">
                        <span className="text-orange-500">B</span>
                        <span className="text-foreground">IC</span>
                    </div>
                </div>
                <p className="text-sm text-foreground-secondary mt-1">
                    Building Information Control
                </p>
            </div>

            <div
                className={clsx(
                    'w-full max-w-sm bg-white rounded-2xl px-8 py-8 transform transition-all duration-300 ease-out',
                    leaving ? 'opacity-0 scale-95' : 'opacity-100 scale-100'
                )}
                style={{ boxShadow: cardShadow }}
            >
                {step === 'email' && (
                    <>
                        <h1 className="text-lg font-bold text-gray-900 mb-4">Sign in</h1>
                        <p className="text-sm text-gray-500 mb-5">
                            Enter your email to receive a verification code.
                        </p>
                        <form onSubmit={submitEmail}>
                            <label
                                htmlFor="login-email"
                                className="block text-sm font-bold text-gray-900 mb-1.5"
                            >
                                Email
                            </label>
                            <div className="relative mb-4">
                                <div className="absolute inset-y-0 left-0 flex items-center pl-3.5 text-gray-400 pointer-events-none">
                                    <MailIcon />
                                </div>
                                <input
                                    id="login-email"
                                    name="email"
                                    type="email"
                                    required
                                    autoComplete="email"
                                    value={email}
                                    onChange={(event) => setEmail(event.target.value)}
                                    placeholder="you@company.com"
                                    className="w-full pl-11 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-full text-sm text-gray-900 placeholder-gray-400 transition-colors focus:outline-none focus:bg-white focus:border-gray-300"
                                />
                            </div>

                            <div ref={turnstileContainerRef} className="flex justify-center mb-5" />

                            {turnstileMisconfigured && (
                                <div className="mb-5 flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3.5 py-2.5 text-sm text-amber-800">
                                    <svg
                                        className="w-4 h-4 mt-0.5 flex-shrink-0"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                        aria-hidden="true"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth="2"
                                            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                        />
                                    </svg>
                                    <span>
                                        Sign-in by email code is unavailable: this environment has
                                        no captcha key configured. Use your username and password,
                                        or contact your administrator.
                                    </span>
                                </div>
                            )}

                            {errorBanner}

                            <button
                                type="submit"
                                disabled={emailDisabled}
                                className={clsx(
                                    'w-full flex items-center justify-center gap-2 rounded-full bg-orange-500 py-3 text-sm font-semibold text-white transition-opacity',
                                    emailDisabled
                                        ? 'opacity-40 cursor-not-allowed'
                                        : 'hover:opacity-90'
                                )}
                            >
                                {requestCode.loading ? (
                                    <>
                                        <SpinnerIcon />
                                        <span>Sending...</span>
                                    </>
                                ) : (
                                    'Send verification code'
                                )}
                            </button>
                        </form>

                        <button
                            type="button"
                            onClick={() => goToStep('password')}
                            className="w-full text-center text-sm text-gray-500 underline mt-5"
                        >
                            Sign in with password instead
                        </button>
                    </>
                )}

                {step === 'code' && (
                    <>
                        <div className="flex items-center gap-2 mb-1">
                            <ShieldIcon />
                            <h1 className="text-lg font-bold text-gray-900">
                                Verify your identity
                            </h1>
                        </div>
                        <p className="text-sm text-gray-500 mb-5">
                            Enter the code we sent to your email.
                        </p>

                        <form onSubmit={submitCode}>
                            <label
                                htmlFor="verify-email"
                                className="block text-sm font-bold text-gray-900 mb-1.5"
                            >
                                Email
                            </label>
                            <input
                                id="verify-email"
                                type="email"
                                value={email}
                                disabled
                                className="w-full mb-4 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-full text-sm text-gray-500 cursor-not-allowed"
                            />

                            <label
                                htmlFor="verify-code"
                                className="block text-sm font-bold text-gray-900 mb-1.5"
                            >
                                Verification code
                            </label>
                            <input
                                id="verify-code"
                                name="code"
                                type="text"
                                required
                                inputMode="numeric"
                                maxLength={6}
                                autoComplete="one-time-code"
                                value={code}
                                onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))}
                                placeholder="------"
                                className="w-full mb-5 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-full text-center text-lg tracking-widest text-gray-900 placeholder-gray-300 transition-colors focus:outline-none focus:bg-white focus:border-gray-300"
                            />

                            {errorBanner}

                            <button
                                type="submit"
                                disabled={codeDisabled}
                                className={clsx(
                                    'w-full flex items-center justify-center gap-2 rounded-full bg-orange-500 py-3 text-sm font-semibold text-white transition-opacity mt-2',
                                    codeDisabled
                                        ? 'opacity-40 cursor-not-allowed'
                                        : 'hover:opacity-90'
                                )}
                            >
                                {verifyCode.loading ? (
                                    <>
                                        <SpinnerIcon />
                                        <span>Verifying...</span>
                                    </>
                                ) : (
                                    'Verify'
                                )}
                            </button>
                        </form>
                    </>
                )}

                {step === 'password' && (
                    <>
                        <h1 className="text-lg font-bold text-gray-900 mb-5">
                            Sign in with password
                        </h1>
                        <form onSubmit={submitPassword}>
                            <div className="mb-4">
                                <label
                                    htmlFor="login-username"
                                    className="block text-sm font-bold text-gray-900 mb-1.5"
                                >
                                    Username
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 flex items-center pl-3.5 text-gray-400 pointer-events-none">
                                        <UserIcon />
                                    </div>
                                    <input
                                        id="login-username"
                                        name="username"
                                        type="text"
                                        required
                                        autoComplete="username"
                                        value={username}
                                        onChange={(event) => setUsername(event.target.value)}
                                        placeholder="Enter your username"
                                        className="w-full pl-11 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-full text-sm text-gray-900 placeholder-gray-400 transition-colors focus:outline-none focus:bg-white focus:border-gray-300"
                                    />
                                </div>
                            </div>

                            <div className="mb-2">
                                <label
                                    htmlFor="login-password"
                                    className="block text-sm font-bold text-gray-900 mb-1.5"
                                >
                                    Password
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 flex items-center pl-3.5 text-gray-400 pointer-events-none">
                                        <LockIcon />
                                    </div>
                                    <input
                                        id="login-password"
                                        name="password"
                                        type="password"
                                        required
                                        autoComplete="current-password"
                                        value={password}
                                        onChange={(event) => setPassword(event.target.value)}
                                        placeholder="Enter your password"
                                        className="w-full pl-11 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-full text-sm text-gray-900 placeholder-gray-400 transition-colors focus:outline-none focus:bg-white focus:border-gray-300"
                                    />
                                </div>
                            </div>

                            {errorBanner}

                            <button
                                type="submit"
                                disabled={passwordDisabled}
                                className={clsx(
                                    'w-full flex items-center justify-center gap-2 rounded-full bg-orange-500 py-3 text-sm font-semibold text-white transition-opacity mt-4',
                                    passwordDisabled
                                        ? 'opacity-40 cursor-not-allowed'
                                        : 'hover:opacity-90'
                                )}
                            >
                                {authUser.loading ? (
                                    <>
                                        <SpinnerIcon />
                                        <span>Signing in...</span>
                                    </>
                                ) : (
                                    'Log In'
                                )}
                            </button>
                        </form>
                    </>
                )}
            </div>

            {step !== 'email' && (
                <button
                    type="button"
                    onClick={() => goToStep('email')}
                    className="mt-5 text-sm"
                    style={{ color: theme === 'dark' ? 'rgba(255,255,255,0.7)' : '#6b7280' }}
                >
                    ← Back to sign in
                </button>
            )}

            <p
                className="mt-4 text-xs"
                style={{ color: theme === 'dark' ? 'rgba(255,255,255,0.5)' : '#9ca3af' }}
            >
                Secure access for your construction team.
            </p>
        </div>
    );
}
